import { ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, inject, input, signal, OnDestroy } from '@angular/core';
import { EntryData, ProviderData, Structure } from '../structure-data.models';
import { TedDomain } from '../structure-data.models';
import { UtilService } from 'src/app/util.service';
import { CommonService } from 'src/app/common.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { ConfigService } from 'src/app/config.service';
import { PlatformService } from 'src/app/platform.service';
import { MolstarPluginService } from 'src/app/molstar-plugin-service';
import { applyAlphaMissenseTooltips, colorByAlphaMissense, resetColoring, resetTooltips, assignColor, } from 'src/app/molstar-alpha-missense';
import { colorByTed, colorByCustomAnnotations } from '../../molstar-alpha-ted';
import { DownloadOption } from '../entry-dropdown/entry-dropdown.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { EntryService } from '../entry.service';
import { combineLatest, filter, map } from 'rxjs';
import { PaeSelectedRegions } from 'src/app/pae-view/pae-view.component';

import { PaeDataService, Chain} from 'src/app/pae-view/pae-data.service';
export interface MappedTedDomain {
  start: number;
  end: number;
  label: string;
  color: string;
  width: number;
  tedNumber: number;
  plddt: number;
  boundaries?: string;
  length?: number;
  qscore: number;
  cath_assignment_level: string;
  quality: string
}
const COUNT_LEGENDS = 12;
@Component({
  selector: 'annotations-tab',
  templateUrl: './annotations-tab.component.html',
  styleUrl: './annotations-tab.component.css',
  standalone: false
})
export class AnnotationsTabComponent implements OnDestroy {
  private store = inject(Store);
  public accession = toSignal(this.store.select(EntrySelectors.accession));

  public entryData = signal<EntryData>({} as EntryData);
  public tedDomainData = signal<TedDomain[]>([]);
  public csvAmData = signal<string>('');
  public uniprotData = signal<any>({});
  public providerData = signal<ProviderData>({} as ProviderData);

  public slicedTedDomain = signal<TedDomain[]>([]);

  public isTedDomain = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  public isCopySequence: boolean = false;
  public pdbeMolstar: any;
  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  public rangeFilter: any;
  public activeDomain = signal<MappedTedDomain>(null);
  public mappedTedDomainData = signal<MappedTedDomain[]>([]);
  public tedDomainNumberForClickActive = signal<number | null>(null);

  public isOldUniprotEntry: boolean = false;
  public plddtData: string;
  public dropdownSelected: string;

  public colorScheme: string;

  public dropdownOptions: DownloadOption[] = [];
  // public dropdownOptions: { label: string, value?: any, isShow?: boolean, isHeader?: boolean, isWarning?:boolean }[];

  constructor(
    private configService: ConfigService,
    private utilService: UtilService,
    public commonService: CommonService,
    private gaService: GoogleAnalyticsService,
    private el: ElementRef,
    private molstarPluginService: MolstarPluginService,
    private changeDetectorRef: ChangeDetectorRef,
    public entryService: EntryService,
  ) { }
  public readonly isSidebarDisplayed = signal<boolean>(true);
  public highlightedSequence: { text: string; highlight: boolean }[] = [];
  public isAlphaMis: boolean = false;
  public isModalConf: boolean = true;

  ngOnInit(): void {
    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(),
      this.store.select(EntrySelectors.tedDomainData).pipe(),
      this.store.select(EntrySelectors.csvAmData).pipe(),
      this.store.select(EntrySelectors.uniprotData).pipe(),
      this.store.select(EntrySelectors.providerData).pipe(),
    ])
      .pipe(
        map(([entryData, tedDomainData, csvAmData, uniprotData, providerData]) => {
          this.entryData.set(entryData);
          this.tedDomainData.set(tedDomainData);
          this.csvAmData.set(csvAmData);
          this.uniprotData.set(uniprotData);
          this.providerData.set(providerData);

          if (this.tedDomainData()) {
            this.slicedTedDomain.update(() =>
              this.tedDomainData()?.slice(0, COUNT_LEGENDS)
            );
            // this.legendText.set(`Show all ${this.tedDomainData()?.length} domains`);
          }
          if (entryData.isUniProt) {
            // if((this.entryData().sequenceVersionDate!= "" || this.entryData().sequenceVersionDate!= null) && (this.uniprotData().sequence.modified != "" || this.uniprotData().sequence.modified != null)){
            //   this.isOldUniprotEntry = this.utilService.areDatesDifferent(this.entryData().sequenceVersionDate, this.uniprotData().sequence.modified);
            // }
          }
          this.colorScheme = 'plddt';
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    this.initMolstar();
    this.getPLDDtData();
  }

  public updateColorScheme(event) {
    let tag = '';
    let gtagmsg = '';
    const scheme = event.scheme;
    this.colorScheme = event.scheme;
    if (scheme.includes('missense')) {
      tag = "colour_by_AM";
      gtagmsg = "Clicks on colour by dropdown and select AlphaMissense";
      this.applyMolstarColoring('missense');
    }
    if (scheme.includes('plddt')) {
      tag = "colour_by_plddt";
      gtagmsg = "Clicks on colour by dropdown and select plddt";
      this.applyMolstarColoring('plddt');
    }
    if (scheme.includes('custom')) {
      tag = "colour_by_domain";
      gtagmsg = "Clicks on colour by dropdown and select domain";
      colorByCustomAnnotations(this.pdbeMolstar, event.trackData);
    }
  }

  public async triggerMolstarEvents(event) {
    const eventType = event.eventType;
    if (eventType === 'mouseout') {
      await this.pdbeMolstar.visual.clearHighlight();
    }
    else if (eventType === 'mouseover') {
      await this.pdbeMolstar.visual.highlight({
        data: [
          {
            start_residue_number: event.detail.start,
            end_residue_number: event.detail.end,
            color: null,
          },
        ],
      })
    }
    else if (eventType === 'click') {
      const selectData = {
        start_residue_number: event.detail.start,
        end_residue_number: event.detail.end,
        color: null,
        representation: event.detail.start === event.detail.end ? 'ball-and-stick' : null,
        focus: true,
      };
      this.pdbeMolstar.visual.select({
        data: [selectData],
        keepColors: true,
      });
    }
    else if (eventType === 'unselect') {
      this.pdbeMolstar.visual.reset({ camera: true });
    }
  }

  async getPLDDtData() {
    const fileUrl = this.entryData().plddtDocUrl;
    const response = await fetch(fileUrl);

    if (!response.ok) return undefined;
    const js = await response.json();
    this.plddtData = js;
  }

  amSelectionUpdate(data): void {
    if (data.dataX > 0) {
      this.handleAmUpdate(data);
    } else {
      this.pdbeMolstar.visual.reset({ camera: true });
    }
  }
  async handleAmUpdate(data): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    const color = assignColor(data.score);
    await this.pdbeMolstar.visual.select({
      data: [
        {
          residue_number: data.dataX,
          color: null,
          representation: 'ball-and-stick',
          representationColor: color,
          focus: true,
        },
      ],
      keepColors: true,
    });

    await this.pdbeMolstar.visual.highlight({
      data: [{ residue_number: data.dataX }],
      // nonSelectedColor: '#000000'
    });
  }

  amSelectionCategory(data): void {
    if (this.isAlphaMis) {
      this.rangeFilter = data.range;
      this.applyMolstarColoring('missense');
    }
  }

  amSelectionClear() {
    this.pdbeMolstar.visual.clearSelection(undefined, { keepColors: true });
    this.pdbeMolstar.visual.reset({ camera: true });
  }

  toggleUpdateFlags(data) {
    this.isAlphaMis = data.isAlphaMis;
    this.isModalConf = data.isModalConf;
  }

  public async onDropdownSelect(event: string) {
    this.dropdownSelected = event;
    let tag = "";
    let gtagmsg = "";
    // const usePlddt = event.includes('Model Confidence') || event.includes('AlphaMissense');
    // this.applyMolstarColoring(usePlddt ? 'plddt' : 'ted');

    if (event.includes('AlphaMissense')) {
      tag = "colour_by_AM";
      gtagmsg = "Clicks on colour by dropdown and select AlphaMissense";
      this.dropdownSelected = `AlphaMissense Pathogenicity`;
      this.applyMolstarColoring('missense');
    }
    if (event.includes('Model Confidence')) {
      tag = "colour_by_plddt";
      gtagmsg = "Clicks on colour by dropdown and select plddt";
      this.dropdownSelected = `Model Confidence - pLDDT`;
      this.applyMolstarColoring('plddt');
    }
    if (event.includes('Domain')) {
      tag = "colour_by_domain";
      gtagmsg = "Clicks on colour by dropdown and select domain";
      this.applyMolstarColoring('ted');
    }

    // for GA tags
    this.gaService.eventEmitter('annotations_colour_3D', 'colour_3D', 'clicks', tag, gtagmsg);
  }

  public toggleSidebar() {
    this.isSidebarDisplayed.update((prev) => !prev);
  }
  public copySequence(sequence) {
    const text = `${sequence}`;
    this.utilService.copy(text);
    this.isCopySequence = true;
    setTimeout(() => {
      this.isCopySequence = false;
    }, 3000);
  }

  public getQuality(qscore: number, assignment: string): string {
    if (qscore >= 75 && assignment.includes("C.A.T.H. Homologous Superfamily")) {
      return "High-confidence"; //2 stars
    }
    if (qscore >= 70 && (assignment.includes("C.A.T.H. Homologous") || assignment.includes("C.A.T. Fold"))) {
      return "Moderate"; //1 star
    }
    return "Uncertain"; // No star/Empty star
  }

  private getBoundaries(similarTeds): string {
    const mappedSimilarTeds = similarTeds.map(
      (item) => item.start + '-' + item.end
    );
    return mappedSimilarTeds.join(', ');
  }

  private getTotalLength(similarTeds) {
    return similarTeds.reduce(
      (acc, current) => acc + (current.end - current.start) + 1,
      0
    );
  }

  clearSeqSelection(): void {
    const selectedSeqElements = this.el.nativeElement.querySelectorAll(
      '.ms-pae-selection, .ms-pae-select-green, .ms-pae-select-orange, .ms-pae-select-yellow'
    );
    if (selectedSeqElements) {
      selectedSeqElements.forEach((selEle) => {
        selEle.classList.remove('ms-pae-selection');
        selEle.classList.remove('ms-pae-select-green');
        selEle.classList.remove('ms-pae-select-orange');
        selEle.classList.remove('ms-pae-select-yellow');
      });
    }
  }

  applySeqSelection(selectionData) {
    this.clearSeqSelection();
    selectionData.forEach((ele) => {
      for (
        let seqid = ele.start_residue_number;
        seqid <= ele.end_residue_number;
        seqid++
      ) {
        const seqElement = this.el.nativeElement.querySelector(
          `.msp-sequence-present[data-seqid="${seqid}"`
        );
        if (seqElement) {
          seqElement.classList.add(`${ele.class}`);
        }
      }
    });
  }

  validateBlankSelection(regions) {
    regions.forEach((select) => {
      if (select.start === 0 && select.end === 0) {
        this.clearSeqSelection();
        this.pdbeMolstar.visual.reset({ camera: true });
      }
    });
    return;
  }

  applyMolstarColoring(kind: 'plddt' | 'missense' | 'ted') {
    if (kind === 'missense') {
      if (this.rangeFilter) {
        colorByAlphaMissense(
          this.pdbeMolstar,
          this.csvAmData(),
          this.rangeFilter
        );
      } else {
        colorByAlphaMissense(this.pdbeMolstar, this.csvAmData());
      }
      applyAlphaMissenseTooltips(this.pdbeMolstar, this.csvAmData());
      this.gaService.eventEmitter(
        'AM_toggle',
        'AlphaMissense',
        'click',
        'AM_toggle',
        'Toggle between AM and pLDDT colouring on Mol*'
      );
    } else if (kind === 'plddt') {
      resetColoring(this.pdbeMolstar);
      resetTooltips(this.pdbeMolstar);
      this.pdbeMolstar.visual.reset({ camera: true });
    } else if (kind === 'ted') {
      // colorByTed(this.pdbeMolstar, this.tedDomainData().filter(domain=>domain.ted_domain_no=== this.activeDomain().tedNumber));
      colorByTed(this.pdbeMolstar, this.tedDomainData());

      this.gaService.eventEmitter(
        'domains_toggle',
        'domains',
        'click',
        'domains_toggle',
        'Toggle between TED colouring on Mol*'
      );
    } else {
      console.error('Unknow coloring kind:', kind);
    }
  }

  cmSelectionUpdate(cmSelectedRegions: PaeSelectedRegions): void {
    this.handleCmUpdate(cmSelectedRegions);
  }

  async handleCmUpdate(regions: PaeSelectedRegions): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    if (!regions) {
      this.clearSeqSelection();
      await this.applyMolstarColoring(this.isAlphaMis ? 'missense' : 'plddt');

      this.pdbeMolstar.visual.reset({ camera: true });
      return;
    }

    const localProteinChains: Chain[] = 
    [
      { name: 'ProteinA', label_asym_id: undefined, length: this.entryData().sequenceEnd, sequenceEnd:this.entryData().sequenceEnd, sequenceStart:1 },
    ]; // TODO get real data from somewhere
    const selectionData = this.utilService.getSelectionData(regions, localProteinChains);

    this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });

    this.applySeqSelection(selectionData);
  }

  initMolstar() {
    this.structure = {
      model_url: this.entryData().bcifUrl,
      model_format: 'cif',
      binary: true,
    };

    // Set last updated date
    const modelCreatedDate = new Date(this.entryData().modelCreatedDate);
    this.lastUpdateDate = `${modelCreatedDate.getDate()} ${modelCreatedDate.toLocaleString(
      'default',
      { month: 'long' }
    )} ${modelCreatedDate.getFullYear()}`;
    this.molstarInitialTimeoutId = setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 10);
  }

  async handleMolstar(structure: Structure): Promise<void> {

    const options = {
      customData: {
        url: structure.model_url,
        format: structure.model_format,
        binary: structure.binary,
      },
      subscribeEvents: false,
      bgColor: { r: 255, g: 255, b: 255 },
      alphafoldView: true,
      reactive: true,
      sequencePanel: true,
      hideCanvasControls: ['animation'],
      landscape: true,
      hideControls: true,
      validationAnnotation: true,
      granularity: 'residue'

    }

    if (this.isDestroyed) return;

    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
    } else {
      const ele = this.el.nativeElement.querySelector('.annotations-molstar-container');

      if (ele) {
        this.molstarInitAttempts = 0;
        // this.pdbeMolstar = new PDBeMolstarPlugin();
        await this.molstarPluginService.loadPlugin();
        // Component may have been destroyed while loadPlugin() (a CDN fetch on first use) was
        // in flight — don't create/render into an element that's since been torn down.
        if (this.isDestroyed) return;
        const pluginInstance = this.molstarPluginService.createInstance();
        this.pdbeMolstar = pluginInstance;
        const layout = [
          { target: ele, component: this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
        ]
        this.pdbeMolstar.render(layout, options);
        // this.pdbeMolstar.render(ele, options);

        // Update Mol load complete flag
        this.pdbeMolstar.events.loadComplete.subscribe((e) => {
          this.molLoadComplete = true;
        });
      } else if (this.molstarInitAttempts < AnnotationsTabComponent.MAX_MOLSTAR_INIT_ATTEMPTS) {
        // .annotations-molstar-container is behind @if (entryData().isComplex === false) — for
        // a cold store load, entryData can still be the default {} at this point even though
        // this.entryData() was already set synchronously in ngOnInit, because Angular hasn't
        // flushed that change into the template yet. Retry instead of silently giving up
        // forever (which left pdbeMolstar undefined for the rest of this component's life,
        // and any later unguarded call like resetColoring(this.pdbeMolstar) would throw
        // "Cannot read properties of undefined (reading 'visual')").
        this.molstarInitAttempts++;
        // Tracked and cleared on destroy — an uncancelled retry firing after teardown would
        // create an orphaned Mol* instance in a detached container: invisible, never disposed,
        // still holding a WebGL context (the same leak class as fixes already made elsewhere
        // in this bug chain, missed here the first time round).
        this.molstarRetryTimeoutId = setTimeout(() => this.handleMolstar(structure), 500);
      }
    }
  }

  private molstarInitAttempts = 0;
  private static readonly MAX_MOLSTAR_INIT_ATTEMPTS = 20;
  private molstarInitialTimeoutId: any;
  private molstarRetryTimeoutId: any;
  private isDestroyed = false;

  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.molstarInitialTimeoutId) clearTimeout(this.molstarInitialTimeoutId);
    if (this.molstarRetryTimeoutId) clearTimeout(this.molstarRetryTimeoutId);
    this.pdbeMolstar?.plugin?.dispose?.();
  }
}
